import { Tooltip, Typography } from '@mui/material';
import { FlexDiv } from 'crono-fe-common/components/Layout/FlexDiv';
import { useMemo } from 'react';
import TemplateType from 'crono-fe-common/types/enums/templateType';
import { Template as _Template } from 'crono-fe-common/types/template';
import { TemplateWrapper } from './styles';
import { useAuth } from 'context/auth';
import parse from 'html-react-parser';
import { ExpandableBox } from '@crono-one/design-system';
import { cn } from 'utils/style-utils';
import { fullName, replaceAllTemplateVariables } from 'crono-fe-common/utils';

import PrivateLockIcon from 'crono-fe-common/icons/Icon-Private-Lock';
import { colors } from 'crono-fe-common/theme';
import { ReactComponent as NotOwnedIcon } from 'crono-fe-common/icons/Not-owned-by-the-user.svg';
import useGetUsers from 'hooks/services/subscription/useGetUsers';
import TagsBoxShowAll from 'components/TagsBoxShowAll';
import useGetTemplateVariables from 'hooks/services/variables/useGetTemplateVariables';
import useGetTemplateVariableExternalProperties from 'hooks/services/externalProperty/useGetTemplateVariableExternalProperties';
import CronoVoiceVisualizer from '../linkedinView/cronoVoiceVisualizer';
import { Account } from 'crono-fe-common/types/account';
import { Prospect } from 'crono-fe-common/types/prospect';
import { Template } from 'crono-fe-common/types/template';

export interface ITemplateProps {
  template: Template;
  showAnalytics: boolean;
  handleSelectTemplate: (template: Template) => void;
  showTags?: boolean | null;
  usedForProspect?: boolean | null;
  selected?: boolean;
  account?: Account | null;
  prospect?: Prospect | null;
  smallCard?: boolean;
}

const TemplateCard = ({
  template,
  showAnalytics,
  handleSelectTemplate,
  showTags,
  selected,
  prospect,
  account,
  usedForProspect,
  smallCard = false,
}: ITemplateProps) => {
  const { user } = useAuth();
  const { data: users } = useGetUsers();

  function renderTemplateAnalytics(template: _Template) {
    const total = template.useCount;
    const replyRate =
      template.answerCount !== 0 && total !== 0
        ? Math.round((template.answerCount / total) * 100)
        : 0;
    const openRate =
      template.openCount !== 0 && total !== 0
        ? Math.round((template.openCount / total) * 100)
        : 0;
    const showOpenRate = template.type === TemplateType.EMAIL;
    return (
      <FlexDiv
        className="template-analytics-container"
        width={showOpenRate ? '180px' : '120px'}
      >
        <Tooltip arrow title="Total use" placement="bottom">
          <div className="template-analytics-item usage-item">{total}</div>
        </Tooltip>
        <div className="vertical-divider" />
        {showOpenRate && (
          <>
            <Tooltip arrow title="Open rate" placement="bottom">
              <div className="template-analytics-item open-item">
                {openRate}%
              </div>
            </Tooltip>
            <div className="vertical-divider" />
          </>
        )}
        <Tooltip
          arrow
          title={`${
            template.type === TemplateType.INVITATION
              ? 'Accepted rate'
              : 'Reply rate'
          }`}
          placement="bottom"
        >
          <div className="template-analytics-item reply-item">{replyRate}%</div>
        </Tooltip>
      </FlexDiv>
    );
  }

  const { data: templateVariables } = useGetTemplateVariables();
  const externalProperties = useGetTemplateVariableExternalProperties();

  const subjectReplaced = useMemo(() => {
    if (!template.subject) return '';
    return parse(
      replaceAllTemplateVariables(
        template.subject,
        prospect ?? null,
        account ?? null,
        user ?? null,
        templateVariables?.data?.data ?? null,
        externalProperties,
        template.type === TemplateType.EMAIL,
      ),
    );
  }, [
    template,
    prospect,
    account,
    user,
    templateVariables,
    externalProperties,
  ]);

  const templateOwner = useMemo(() => {
    return users?.data?.data?.find((u) => u.id === template.userId);
  }, [users, template]);

  return (
    <TemplateWrapper
      id="template-card-wrapper"
      key={template.id}
      onClick={() => handleSelectTemplate(template)}
      selected={selected}
    >
      <FlexDiv
        id="template-card"
        justifyContent="space-between"
        alignItems="start"
        direction="column"
        height="fit-content"
        style={{
          rowGap: template.type === TemplateType.LINKEDIN ? 0 : 12,
          opacity: usedForProspect ? '0.4' : '1',
        }}
      >
        {showTags && (
          <FlexDiv
            justifyContent="space-between"
            width="100%"
            style={{ gap: '4px', alignItems: 'center' }}
          >
            {template.strategyStepTemplateId && (
              <Typography
                fontWeight={500}
                fontSize={'12px'}
                color={colors.purpleHover}
                className="ab-tag"
              >
                A/B
              </Typography>
            )}

            {!template.shared && (
              <Tooltip arrow title={'Private template'} placement="top">
                <div
                  style={{
                    display: 'flex',
                    height: 'fit-content',
                    width: 'fit-content',
                  }}
                >
                  <PrivateLockIcon />
                </div>
              </Tooltip>
            )}
            {template.userId !== user?.id && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  paddingBottom: '2px',
                }}
              >
                <Tooltip
                  arrow
                  title={
                    template.userId
                      ? `The owner is ${fullName(templateOwner?.firstName, templateOwner?.lastName) ?? 'deleted user'}`
                      : 'No owner'
                  }
                  placement="top"
                >
                  <div
                    style={{
                      display: 'flex',
                      height: 'fit-content',
                      width: 'fit-content',
                    }}
                  >
                    <NotOwnedIcon className="not-owned-icon" />
                  </div>
                </Tooltip>
              </div>
            )}
            <Tooltip
              arrow
              title={
                usedForProspect ? 'Previously used template' : template.title
              }
              enterDelay={400}
            >
              <Typography fontWeight={600} fontSize={14} noWrap width={'100%'}>
                {template.title}
              </Typography>
            </Tooltip>

            {showAnalytics && renderTemplateAnalytics(template)}
          </FlexDiv>
        )}
        <FlexDiv
          justifyContent="start"
          alignItems="start"
          direction="column"
          style={{ overflow: 'hidden', gap: '0px' }}
        >
          {template.type === TemplateType.EMAIL && (
            <Tooltip
              arrow
              title={subjectReplaced}
              placement="bottom"
              style={{
                width: 'fit-content',
                maxWidth: '100%',
                overflow: 'hidden',
              }}
              enterDelay={800}
              enterNextDelay={800}
            >
              <Typography
                fontWeight={500}
                fontSize={14}
                noWrap
                className="template-title-display"
              >
                Subject: {subjectReplaced}
              </Typography>
            </Tooltip>
          )}
          {template.type === TemplateType.VOICE_NOTE ? (
            <FlexDiv height={'fit-content'} padding={'0px 15px 0px 0px'}>
              <CronoVoiceVisualizer
                templateId={template.id}
                duration={template.recordingDuration}
                allowToRemove={false}
                size={smallCard ? 'small' : 'medium'}
              />
            </FlexDiv>
          ) : (
            <ExpandableBox
              className={cn(
                'border-transparent bg-transparent p-2 text-gray-700 [&_p]:m-0',
                template.type === TemplateType.LINKEDIN && 'pb-5',
              )}
            >
              {parse(
                replaceAllTemplateVariables(
                  template.content,
                  prospect ?? null,
                  account ?? null,
                  user ?? null,
                  templateVariables?.data?.data ?? null,
                  externalProperties,
                  template.type === TemplateType.EMAIL,
                ),
              )}
            </ExpandableBox>
          )}
        </FlexDiv>
        <FlexDiv justifyContent="space-between" width="100%">
          {showTags ? (
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 6,
              }}
            >
              <TagsBoxShowAll
                type="AccountAndProspect"
                blockIsEditable={true}
                disableEdit={true}
                showRemovable={false}
                currentSituationTags={
                  template?.templateTags?.map((tag) => {
                    return {
                      externalPropertyId: tag.externalPropertyId,
                      option: tag.value,
                    };
                  }) ?? []
                }
              />
            </div>
          ) : (
            <Tooltip
              arrow
              title={
                usedForProspect ? 'Previously used template' : template.title
              }
              enterDelay={400}
            >
              <Typography
                fontWeight={600}
                fontSize={14}
                noWrap
                className="template-title-display"
              >
                {template.title}
              </Typography>
            </Tooltip>
          )}
        </FlexDiv>
      </FlexDiv>
    </TemplateWrapper>
  );
};

export default TemplateCard;
